import { Fragment, useEffect, useState } from "react";
import { getAdminHeaders } from "./adminApi";
import { assetUrl } from "../../utils/api";

const STATUS_OPTIONS = [
  { value: "", label: "全部状态" },
  { value: "pending", label: "待审核" },
  { value: "rejected", label: "未通过" },
  { value: "published", label: "在售" },
  { value: "reserved", label: "已预留" },
  { value: "done", label: "已成交" },
  { value: "taken_down", label: "已下架" },
];

export default function AdminSecondhand() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("pending");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [notes, setNotes] = useState({});
  const pageSize = 20;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (status) params.set("status", status);
      if (keyword.trim()) params.set("keyword", keyword.trim());
      const res = await fetch(`/api/secondhand/v1/admin/listings?${params}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "加载失败");
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setMessage(e.message || "加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  const update = async (id, patch) => {
    setMessage("");
    try {
      const res = await fetch(`/api/secondhand/v1/admin/listings/${id}`, {
        method: "PATCH",
        headers: getAdminHeaders(true),
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "更新失败");
      setItems((prev) => prev.map((row) => (row.id === id ? data.item : row)));
      setMessage("已更新");
    } catch (e) {
      setMessage(e.message || "更新失败");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">闲置交换管理</h2>
        <p className="text-sm text-gray-500 mt-1">
          会员发布后进入「待审核」；通过后才会在前台公开展示。也可下架违规内容。
        </p>
      </div>

      <div className="bg-white rounded-2xl border p-4 flex flex-wrap gap-3 items-end">
        <label className="text-sm">
          <span className="text-gray-500 block mb-1">状态</span>
          <select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
            className="border rounded-xl px-3 py-2 min-w-[140px]"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || "all"} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm flex-1 min-w-[200px]">
          <span className="text-gray-500 block mb-1">搜索标题 / 城市</span>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                load();
              }
            }}
            className="border rounded-xl px-3 py-2 w-full"
          />
        </label>
        <button
          type="button"
          onClick={() => {
            setPage(1);
            load();
          }}
          className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
        >
          查询
        </button>
      </div>

      {message && <div className="text-sm text-blue-700 bg-blue-50 px-4 py-2 rounded-xl">{message}</div>}

      <div className="bg-white rounded-2xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3">ID</th>
              <th className="px-4 py-3">闲置</th>
              <th className="px-4 py-3">发布者</th>
              <th className="px-4 py-3">城市</th>
              <th className="px-4 py-3">状态</th>
              <th className="px-4 py-3">时间</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  加载中...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  暂无数据
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <Fragment key={item.id}>
                  <tr className="border-t align-top">
                    <td className="px-4 py-3 text-gray-400">{item.id}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3">
                        {item.cover_url ? (
                          <img
                            src={assetUrl(item.cover_url)}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : null}
                        <div>
                          <div className="font-medium text-gray-800">{item.title}</div>
                          <div className="text-xs text-gray-400">
                            {item.category_label} · {item.intent_label}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.seller_name || "—"}
                      <div className="text-xs text-gray-400">UID {item.user_id}</div>
                    </td>
                    <td className="px-4 py-3">{item.location_label || "—"}</td>
                    <td className="px-4 py-3">
                      <select
                        value={item.status}
                        onChange={(e) => update(item.id, { status: e.target.value })}
                        className="border rounded-lg px-2 py-1.5"
                      >
                        {STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {item.created_at ? item.created_at.replace("T", " ").slice(0, 16) : "—"}
                    </td>
                    <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() => {
                          setExpandedId(expandedId === item.id ? null : item.id);
                          setNotes((prev) => ({ ...prev, [item.id]: item.admin_note || "" }));
                        }}
                      >
                        {expandedId === item.id ? "收起" : "详情"}
                      </button>
                      {item.status === "pending" && (
                        <>
                          <button
                            type="button"
                            className="text-green-600 hover:underline"
                            onClick={() => update(item.id, { status: "published" })}
                          >
                            通过
                          </button>
                          <button
                            type="button"
                            className="text-amber-600 hover:underline"
                            onClick={() => update(item.id, { status: "rejected" })}
                          >
                            拒绝
                          </button>
                        </>
                      )}
                      {item.status !== "taken_down" && item.status !== "rejected" && (
                        <button
                          type="button"
                          className="text-red-500 hover:underline"
                          onClick={() => update(item.id, { status: "taken_down" })}
                        >
                          下架
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedId === item.id && (
                    <tr className="bg-slate-50 border-t">
                      <td colSpan={7} className="px-4 py-4 space-y-3">
                        <p className="text-sm text-gray-700 whitespace-pre-line">{item.description || "无说明"}</p>
                        <div className="flex flex-col md:flex-row gap-2">
                          <textarea
                            value={notes[item.id] ?? ""}
                            onChange={(e) =>
                              setNotes((prev) => ({ ...prev, [item.id]: e.target.value }))
                            }
                            rows={2}
                            className="flex-1 border rounded-xl px-3 py-2 text-sm"
                            placeholder="管理员备注"
                          />
                          <button
                            type="button"
                            onClick={() => update(item.id, { admin_note: notes[item.id] || "" })}
                            className="px-4 py-2 rounded-xl bg-gray-800 text-white text-sm"
                          >
                            保存备注
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between text-sm text-gray-500">
        <span>
          共 {total} 条 · {page}/{totalPages}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 border rounded-lg disabled:opacity-40"
          >
            上一页
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 border rounded-lg disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      </div>
    </div>
  );
}
